/**
 * MetafieldColorSwatches custom element.
 *
 * Hover  — previews the linked product's images on ALL card slides + title/link.
 *           Restores to the last-clicked (persisted) state on pointer-leave.
 * Click  — selects the color in-place: updates ALL slides, title, price and card link
 *           without navigating away. Notifies quick-add to refetch the new product.
 * On PDP  — no product-card parent, so click falls through to normal anchor navigation.
 */
class MetafieldColorSwatches extends HTMLElement {
  /** @type {HTMLElement | null} */
  #productCard = null;

  /** @type {boolean} */
  #isHovering = false;

  /**
   * "Resting" state: what the card shows when nothing is being hovered.
   * imgs: src for each non-hidden slideshow slide in DOM order.
   * @type {{ imgs: string[], href: string | null, title: string | null, titleHref: string | null }}
   */
  #persisted = { imgs: [], href: /** @type {string | null} */ (null), title: /** @type {string | null} */ (null), titleHref: /** @type {string | null} */ (null) };

  get #hoverEnabled() {
    return this.dataset.hover !== 'false';
  }

  get #swatches() {
    return this.querySelectorAll('.metafield-swatch__link');
  }

  connectedCallback() {
    this.#productCard = this.closest('product-card');

    if (this.#productCard) {
      // Snapshot current DOM state so hover-leave always has something to restore to
      this.#persisted = {
        imgs: this.#getSlideImages(),
        href: this.#getCardLink()?.getAttribute('href') ?? null,
        title: this.#getTitleEl()?.textContent?.trim() ?? null,
        titleHref: this.#getTitleLinkEl()?.getAttribute('href') ?? null,
      };
    }

    for (const swatch of this.#swatches) {
      swatch.addEventListener('pointerenter', /** @type {EventListener} */ (this.#onEnter));
      swatch.addEventListener('pointerleave', this.#onLeave);
      swatch.addEventListener('click', /** @type {EventListener} */ (this.#onClick));
    }
  }

  disconnectedCallback() {
    for (const swatch of this.#swatches) {
      swatch.removeEventListener('pointerenter', /** @type {EventListener} */ (this.#onEnter));
      swatch.removeEventListener('pointerleave', this.#onLeave);
      swatch.removeEventListener('click', /** @type {EventListener} */ (this.#onClick));
    }
  }

  // ─── DOM helpers ────────────────────────────────────────────────────────────

  /**
   * Returns all non-hidden slideshow slides in the card.
   * @returns {Element[]}
   */
  #getSlides() {
    return Array.from(this.#productCard?.querySelectorAll('slideshow-slide:not([hidden])') ?? []);
  }

  /**
   * Snapshots the current src of every slide's img element.
   * @returns {string[]}
   */
  #getSlideImages() {
    return this.#getSlides().map(slide => slide.querySelector('img')?.getAttribute('src') ?? '');
  }

  /** @returns {HTMLImageElement | null} */
  #getActiveImage() {
    // Prefer the active slideshow slide; fall back progressively
    const activeSlide = this.#productCard?.querySelector('slideshow-slide[aria-hidden="false"]');
    return (
      activeSlide?.querySelector('img') ||
      this.#productCard?.querySelector('card-gallery img') ||
      this.#productCard?.querySelector('.card-gallery img') ||
      this.#productCard?.querySelector('[data-primary-image]') ||
      this.#productCard?.querySelector('.media img') ||
      this.#productCard?.querySelector('img') ||
      null
    );
  }

  /** @returns {HTMLAnchorElement | null} */
  #getCardLink() {
    return this.#productCard?.querySelector('a.product-card__link') ?? null;
  }

  /** @returns {HTMLAnchorElement | null} */
  #getTitleLinkEl() {
    return this.#productCard?.querySelector('[ref="productTitleLink"]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getTitleEl() {
    const titleLink = this.#getTitleLinkEl();
    return (
      titleLink?.querySelector('p') ||
      this.#productCard?.querySelector('[data-product-title]') ||
      this.#productCard?.querySelector('.product-title a') ||
      this.#productCard?.querySelector('h1 a, h2 a, h3 a, h4 a') ||
      null
    );
  }

  /**
   * Price element: mirrors product-card.js which uses the LAST priceContainer.
   * @returns {HTMLElement | null}
   */
  #getPriceEl() {
    const containers = this.#productCard?.querySelectorAll("[ref='priceContainer']");
    const container = containers?.[containers.length - 1] ?? null;
    return (
      container?.querySelector('.price__regular .price') ||
      container?.querySelector('.price') ||
      null
    );
  }

  /** @returns {Element | null} */
  #getQuickAdd() {
    return this.#productCard?.querySelector('quick-add-component') ?? null;
  }

  // ─── Card updates ────────────────────────────────────────────────────────────

  /**
   * Write a swatch's product data into the card DOM.
   * Updates every non-hidden slideshow slide with the corresponding product image.
   * @param {{ productImages?: string, productUrl?: string, productTitle?: string }} data
   */
  #writeToCard({ productImages, productUrl, productTitle }) {
    const slides = this.#getSlides();
    const urls = (productImages ?? '').split(',').filter(Boolean);

    if (urls.length > 0) {
      slides.forEach((slide, i) => {
        const img = slide.querySelector('img');
        if (!img) return;
        // Use the matching image for this slide, falling back to the first if the product
        // has fewer images than the card has rendered slides.
        const url = urls[i] ?? urls[0];
        img.setAttribute('src', url);
        img.setAttribute('srcset', url);
      });
    }

    const link = this.#getCardLink();
    if (link && productUrl) link.setAttribute('href', productUrl);

    const titleLink = this.#getTitleLinkEl();
    if (titleLink && productUrl) titleLink.setAttribute('href', productUrl);

    const titleEl = this.#getTitleEl();
    if (titleEl && productTitle) titleEl.textContent = productTitle;
  }

  /** Restore every slide to the last-clicked (persisted) state. */
  #restorePersisted() {
    const slides = this.#getSlides();
    slides.forEach((slide, i) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const src = this.#persisted.imgs[i] ?? this.#persisted.imgs[0];
      if (src) {
        img.setAttribute('src', src);
        img.removeAttribute('srcset');
      }
    });

    const link = this.#getCardLink();
    if (link && this.#persisted.href !== null) link.setAttribute('href', this.#persisted.href);

    const titleLink = this.#getTitleLinkEl();
    if (titleLink && this.#persisted.titleHref !== null) titleLink.setAttribute('href', this.#persisted.titleHref);

    const titleEl = this.#getTitleEl();
    if (titleEl && this.#persisted.title !== null) titleEl.textContent = this.#persisted.title;
  }

  // ─── Event handlers ──────────────────────────────────────────────────────────

  #onEnter = (/** @type {PointerEvent} */ event) => {
    if (!this.#hoverEnabled || !this.#productCard) return;
    this.#isHovering = true;
    const swatch = /** @type {HTMLElement} */ (event.currentTarget);
    this.#writeToCard(swatch.dataset);
  };

  #onLeave = () => {
    if (!this.#hoverEnabled || !this.#productCard) return;
    this.#isHovering = false;
    this.#restorePersisted();
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    // On PDP there is no product-card — allow normal anchor navigation
    if (!this.#productCard) return;

    event.preventDefault();
    event.stopPropagation();

    const swatch = /** @type {HTMLElement} */ (event.currentTarget);

    // Skip if this swatch is already the active selection
    if (swatch.classList.contains('metafield-swatch__link--active')) return;

    const { productImages, productUrl, productTitle, productPrice } = swatch.dataset;

    // 1. Update active swatch indicator
    for (const s of this.#swatches) {
      s.classList.toggle('metafield-swatch__link--active', s === swatch);
      s.setAttribute('aria-current', s === swatch ? 'true' : 'false');
    }

    // 2. Persist the new resting state (full image list for all slides)
    const urls = (productImages ?? '').split(',').filter(Boolean);
    const slides = this.#getSlides();
    this.#persisted = {
      imgs: slides.map((_, i) => urls[i] ?? urls[0] ?? this.#persisted.imgs[i] ?? ''),
      href: productUrl ?? this.#persisted.href,
      title: productTitle ?? this.#persisted.title,
      titleHref: productUrl ?? this.#persisted.titleHref,
    };

    // 3. Update the card DOM immediately (skip if mid-hover to avoid flash)
    if (!this.#isHovering) {
      this.#writeToCard(swatch.dataset);
    }

    // 4. Update the price display
    if (productPrice) {
      const priceEl = this.#getPriceEl();
      if (priceEl) priceEl.textContent = productPrice;
    }

    // 5. Tell quick-add to prefetch the new product so the dialog is ready
    if (productUrl) {
      const quickAdd = /** @type {{ fetchProductPage?: (url: string) => void } | null} */ (this.#getQuickAdd());
      if (quickAdd && typeof quickAdd.fetchProductPage === 'function') {
        quickAdd.fetchProductPage(productUrl);
      }
    }
  };
}

if (!customElements.get('metafield-color-swatches')) {
  customElements.define('metafield-color-swatches', MetafieldColorSwatches);
}
